import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import { ResourceLoading } from "./ResourceLoading";
import PMListboxFormik from "./PMListboxFormik";
import { PMField } from "./PMField";
import { ResourceError } from "./ResourceError";
import {
  fetchLabelTypes,
  fetchLabelPdf as fetchLabelPdfApi,
} from "../../api/company";
import { removeHtml } from "../Utils";

const dashboardOriginForQr =
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://promang.no";

export const LabelPrintDialog = (props) => {
  const {
    onLoading,
    onClose,
    open,
    item,
    items,
    labelEntityType = "product",
  } = props;
  const { t } = useTranslation();
  /** Types fetch only — must not unmount Formik during PDF generation */
  const [typesLoading, setTypesLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Stable list reference: a fresh `item ? [item] : []` or dummy `{ id }` every render
  // made [selectedType, products, props] effect run every time → setAutofilledFields loop.
  const products = useMemo(() => {
    let p =
      items && Array.isArray(items) ? items : item ? [item] : [];
    if (
      p.length === 0 &&
      (props?.title ||
        props?.subtitle ||
        props?.product_id ||
        props?.id)
    ) {
      return [
        {
          id: props?.product_id || props?.id,
          product_id: props?.product_id || props?.id,
        },
      ];
    }
    return p;
  }, [
    items,
    item,
    props?.title,
    props?.subtitle,
    props?.product_id,
    props?.id,
  ]);

  // Determine if fields should be editable (only when exactly 1 product is selected)
  const isSingleProduct = products.length === 1;

  const initialValues = {
    type_id: "",
  };

  const [typeOptions, setTypeOptions] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [error, setError] = useState(null);
  const [currentTypeId, setCurrentTypeId] = useState(null);
  const [autofilledFields, setAutofilledFields] = useState({});
  const fieldsInitializedRef = useRef(false);

  // Fetch label types when dialog opens
  useEffect(() => {
    if (open) {
      getLabelTypes();
    }
  }, [open]);

  const getLabelTypes = async () => {
    setTypesLoading(true);
    try {
      const response = await fetchLabelTypes();
      const data = response.data.data;

      // Convert object to array using Object.values
      const labelTypes = Object.values(data);

      const formattedOptions = labelTypes.map((type) => ({
        value: type,
        label: type.name,
        name: "type_id",
      }));

      setTypeOptions(formattedOptions);
    } catch (err) {
      console.error("Error fetching label types:", err);
      setError("Failed to fetch label types");
    } finally {
      setTypesLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setError(null);
      setPdfUrl(null);
      setSelectedType(null);
      setCurrentTypeId(null);
      setTypesLoading(false);
      setPdfLoading(false);
      setAutofilledFields({});
      fieldsInitializedRef.current = false;
    }
  }, [open]);

  // Reset fieldsInitialized when selectedType changes
  useEffect(() => {
    fieldsInitializedRef.current = false;
  }, [selectedType]);

  // Calculate autofilled fields when selectedType or products change
  useEffect(() => {
    if (selectedType && products.length > 0) {
      const fields = {};
      const product = products[0]; // For single product, use first one
      
      // Check if this is coming from warehouse-stock (has product.product structure)
      const isFromWarehouseStock = products.some(p => p.product !== undefined);
      
      selectedType.fields.forEach((field) => {
        // Skip document_id when printing from warehouse-stock
        if (field === 'document_id' && isFromWarehouseStock) {
          return; // Don't autofill document_id for warehouse-stock prints
        }
        
        let value = "";

        if (labelEntityType === "tool" && product) {
          switch (field) {
            case "title":
              value = product.tool_number || "";
              break;
            case "subtitle":
              value = product.title || "";
              break;
            case "description": {
              const parts = [product.model, product.serial_number].filter(
                Boolean
              );
              value = parts.join(" · ");
              break;
            }
            case "description_two":
              value =
                product.brand?.name ||
                product.category?.name ||
                "";
              break;
            case "qr_code": {
              const tid = product.id;
              value = tid
                ? `${dashboardOriginForQr}/dashboard/tools/${tid}`
                : "";
              break;
            }
            case "document_id":
              value = "";
              break;
            default:
              value = "";
          }
        }
        
        // First try to get from product object (warehouse-stock format)
        if (!value && product.product) {
          switch (field) {
            case 'title':
              value = product.product.item_number || "";
              break;
            case 'subtitle':
              value = product.product.title || "";
              break;
            case 'description':
              value = product.product.short_description || "";
              break;
            case 'description_two':
              value = product.product.brand?.title || "";
              break;
            case 'qr_code': {
              const productId = product.product.id || product.product.product_id || product.id || product.product_id;
              value = productId
                ? `${dashboardOriginForQr}/dashboard/products/${productId}`
                : "";
              break;
            }
            case 'document_id':
              // Skip document_id for warehouse-stock (already handled above, but keeping for clarity)
              value = "";
              break;
          }
        }
        
        // If not found, try direct product properties (single product format)
        // For title, prioritize item_number over title
        if (!value) {
          if (field === 'title') {
            value = product.item_number || product.title || product[field] || "";
          } else if (product[field]) {
            value = product[field];
          }
        }
        
        // If still not found, try props (product.js format)
        // For title, prioritize item_number prop over title prop
        if (!value) {
          if (field === 'title') {
            value = props?.item_number || props?.title || props?.[field] || "";
          } else if (props?.[field]) {
            value = props[field];
          }
        }
        
        // Final fallback based on field type
        if (!value) {
          switch (field) {
            case 'title':
              // Title should be item_number if available, otherwise title
              value = product.item_number || product.title || "";
              break;
            case 'subtitle':
              value = product.title || product.item_number || "";
              break;
            case 'description':
              value = product.short_description || "";
              break;
            case 'description_two':
              value = product.brand?.title || "";
              break;
            case 'qr_code': {
              const productId = product.id || product.product_id;
              value = productId
                ? `${dashboardOriginForQr}/dashboard/products/${productId}`
                : "";
              break;
            }
            case 'document_id':
              value = product.main_picture?.id || product.main_picture || "";
              break;
          }
        }
        
        // Remove HTML from text fields (except qr_code and document_id which are not text)
        if (field !== 'qr_code' && field !== 'document_id' && value) {
          value = removeHtml(value);
        }
        
        fields[field] = value || "";
      });
      
      setAutofilledFields(fields);
    } else {
      setAutofilledFields({});
    }
  }, [
    selectedType,
    products,
    labelEntityType,
    props?.item_number,
    props?.title,
    props?.subtitle,
    props?.description,
    props?.description_two,
    props?.qr_code,
    props?.product_id,
    props?.id,
  ]);

  const handleDownloadPDF = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  const getLabelPdf = async (values) => {
    setPdfLoading(true);
    setError(null);

    if (!selectedType) {
      setError("Label type is missing.");
      setPdfLoading(false);
      return;
    }

    if (products.length === 0) {
      setError("No products selected.");
      setPdfLoading(false);
      return;
    }

    try {
      // Check if this is coming from warehouse-stock (has product.product structure)
      const isFromWarehouseStock = products.some(p => p.product !== undefined);
      
      // Build product data array - use values from form (user-edited or autofilled)
      const productDataArray = products.map((product) => {
        const productId = product.product_id || product.id;
        const productData = {};
        if (labelEntityType !== "tool" && productId != null) {
          productData.product_id = productId;
        }

        selectedType.fields.forEach((field) => {
          if (field === "document_id" && isFromWarehouseStock) {
            return;
          }

          let value = "";

          if (field === "qr_code") {
            if (labelEntityType === "tool") {
              const tid = product.id;
              value = tid
                ? `${dashboardOriginForQr}/dashboard/tools/${tid}`
                : "";
            } else if (products.length === 1) {
              // Prefer explicit URL from form or autofill (e.g. service reports via props.qr_code)
              const raw =
                values[field] !== undefined &&
                String(values[field]).trim() !== ""
                  ? values[field]
                  : autofilledFields[field];
              const trimmed =
                raw != null && String(raw).trim() !== ""
                  ? String(raw).trim()
                  : "";
              if (trimmed) {
                value = trimmed;
              } else {
                const prodId =
                  product.product_id ||
                  product.id ||
                  product.product?.id ||
                  product.product?.product_id;
                value = prodId
                  ? `${dashboardOriginForQr}/dashboard/products/${prodId}`
                  : "";
              }
            } else {
              const prodId =
                product.product_id ||
                product.id ||
                product.product?.id ||
                product.product?.product_id;
              value = prodId
                ? `${dashboardOriginForQr}/dashboard/products/${prodId}`
                : "";
            }
          } else if (labelEntityType === "tool") {
            if (products.length === 1) {
              value =
                values[field] !== undefined
                  ? values[field]
                  : autofilledFields[field] || "";
            } else {
              if (field === "title") {
                value = product.tool_number || "";
              } else if (field === "subtitle") {
                value = product.title || "";
              } else if (field === "description") {
                value = [product.model, product.serial_number]
                  .filter(Boolean)
                  .join(" · ");
              } else if (field === "description_two") {
                value =
                  product.brand?.name || product.category?.name || "";
              } else {
                value = product[field] || "";
              }
            }
          } else {
            if (products.length === 1) {
              value =
                values[field] !== undefined
                  ? values[field]
                  : autofilledFields[field] || "";
            } else {
              if (product.product) {
                switch (field) {
                  case "title":
                    value = product.product.item_number || "";
                    break;
                  case "subtitle":
                    value = product.product.title || "";
                    break;
                  case "description":
                    value = product.product.short_description || "";
                    break;
                  case "description_two":
                    value = product.product.brand?.title || "";
                    break;
                  case "document_id":
                    value = "";
                    break;
                  default:
                    value = "";
                }
              } else {
                if (field === "title") {
                  value = product.item_number || product.title || "";
                } else {
                  value = product[field] || "";
                }
              }
            }
          }

          if (value || field === "qr_code") {
            productData[field] = value || "";
          }
        });

        return productData;
      });

      const params = {
        type_id: selectedType.id,
        product_data: productDataArray,
      };

      const response = await fetchLabelPdfApi(params);

      if (response?.data?.pdf_url) {
        setPdfUrl(response.data.pdf_url);
        setCurrentTypeId(selectedType.id);
      } else {
        setError("PDF URL is missing in the response.");
      }
    } catch (err) {
      console.error("Error fetching label PDF:", err);
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSubmit = (values) => {
    getLabelPdf(values);
  };

  // Fields are autofilled in background, no need to update form values


  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {products.length > 1
                        ? t("Print Labels") + ` (${products.length})`
                        : t("Print Label")}
                    </Dialog.Title>

                    {typesLoading ? (
                      <ResourceLoading />
                    ) : (
                      <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                      >
                        {({ values, setFieldValue }) => {
                          // Update form values when autofilledFields change (when label type is selected)
                          // Use ref to avoid infinite re-renders
                          if (selectedType && Object.keys(autofilledFields).length > 0 && !fieldsInitializedRef.current) {
                            selectedType.fields.forEach((field) => {
                              // Only set if field doesn't already have a value (preserve user edits)
                              if (values[field] === undefined || values[field] === "") {
                                let fieldValue = autofilledFields[field] || "";
                                // Remove HTML from text fields (except qr_code and document_id)
                                if (field !== 'qr_code' && field !== 'document_id' && fieldValue) {
                                  fieldValue = removeHtml(fieldValue);
                                }
                                setFieldValue(field, fieldValue);
                              }
                            });
                            fieldsInitializedRef.current = true;
                          }
                          
                          return (
                            <Form className="relative">
                              {pdfLoading && (
                                <div
                                  className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-[1px]"
                                  aria-busy="true"
                                  aria-live="polite"
                                >
                                  <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-10 w-10 mb-3 animate-spin border-t-indigo-600" />
                                  <span className="text-sm font-medium text-gray-700">
                                    {t("Generating...")}
                                  </span>
                                </div>
                              )}
                              <div>
                                {error && (
                                  <div className="mt-10">
                                    <ResourceError />
                                  </div>
                                )}
                                <div className="grid grid-cols-6 gap-6 mt-10">
                                  <div className="col-span-6 sm:col-span-6">
                                    <PMListboxFormik
                                      label={t("Label Type")}
                                      name="type_id"
                                      options={typeOptions}
                                      selected={selectedType}
                                      onChange={(value) => {
                                        setSelectedType(value?.value);
                                        setFieldValue(
                                          "type_id",
                                          value?.value?.id
                                        );
                                        // Reset field values when label type changes
                                        if (value?.value?.fields) {
                                          value.value.fields.forEach((field) => {
                                            setFieldValue(field, "");
                                          });
                                        }
                                        if (
                                          value?.value?.id !== currentTypeId
                                        ) {
                                          setPdfUrl(null);
                                        }
                                      }}
                                      value={values.type_id}
                                    />
                                  </div>

                                  {products.length > 1 && (
                                    <div className="col-span-6 sm:col-span-6">
                                      <div className="text-sm text-gray-600">
                                        {t("Selected products")}: {products.length}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Show editable fields when a label type is selected */}
                                  {/* Fields are only visible when exactly 1 product is selected */}
                                  {selectedType && selectedType.fields && selectedType.fields.length > 0 && isSingleProduct && (
                                    <div className="col-span-6 sm:col-span-6 mt-4">
                                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                                          {t("Label Fields")}:
                                        </h4>
                                        {!isSingleProduct && (
                                          <div className="mb-3 text-sm text-gray-600">
                                            {t("Fields are read-only when multiple products are selected")}
                                          </div>
                                        )}
                                        <div className="space-y-4">
                                          {selectedType.fields.map((field) => {
                                            // Remove HTML from displayed value (except qr_code and document_id)
                                            let displayValue = values[field] || "";
                                            if (field !== 'qr_code' && field !== 'document_id' && displayValue) {
                                              displayValue = removeHtml(displayValue);
                                            }
                                            
                                            return (
                                              <div key={field}>
                                                <PMField
                                                  label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                  name={field}
                                                  type="text"
                                                  value={displayValue}
                                                  onChange={(e) => {
                                                    setFieldValue(field, e.target.value);
                                                  }}
                                                  onBlur={() => {}}
                                                  touched={{}}
                                                  errors={{}}
                                                  formik={false}
                                                  readOnly={!isSingleProduct}
                                                  disabled={!isSingleProduct}
                                                  className="w-full"
                                                />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="col-span-6 sm:col-span-6 mb-20">
                                    {pdfUrl &&
                                      selectedType?.id === currentTypeId && (
                                        <button
                                          type="button"
                                          className="w-full justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                          onClick={handleDownloadPDF}
                                        >
                                          {products.length > 1
                                            ? t("Download Labels PDF")
                                            : t("Download Label")}
                                        </button>
                                      )}
                                  </div>
                                </div>
                                <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                  <button
                                    type="submit"
                                    className="inline-flex justify-center w-full rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-60"
                                    disabled={products.length === 0 || pdfLoading}
                                  >
                                    {products.length > 1
                                      ? t("Generate PDF for Selected Products")
                                      : t("Generate PDF")}
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                    onClick={onClose}
                                  >
                                    {t("Close")}
                                  </button>
                                </div>
                              </div>
                            </Form>
                          );
                        }}
                      </Formik>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
